import {
  useEditor,
  EditorContent,
  Editor,
  NodeViewWrapper,
  NodeViewProps,
  ReactNodeViewRenderer,
  type Extensions,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { videoLinkOf } from "@ckgo2/shared";
import Youtube from "@tiptap/extension-youtube";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo,
  Redo,
  Heading1,
  Heading2,
  Quote,
  Code,
  ImageIcon,
  Youtube as YoutubeIcon,
  Minus,
  Pencil,
  Trash2,
  Play,
  Sparkles,
} from "lucide-react";
import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { BanhMiMomentMark } from "./BanhMiMomentMark";
import { MediaNode } from "./MediaNode";
import MediaPicker from "@/components/common/MediaPicker";

const BM_MARK_NAME = BanhMiMomentMark.name;

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  compact?: boolean;
}

const EDITOR_STYLES = `
  .ProseMirror img.ProseMirror-selectednode {
    outline: 3px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 0.5rem;
  }
  .ProseMirror img {
    cursor: pointer;
  }
  .ProseMirror img:hover {
    outline: 2px dashed #93c5fd;
    outline-offset: 2px;
  }
  .ProseMirror img.ProseMirror-selectednode:hover {
    outline: 3px solid #3b82f6;
  }
  .ProseMirror .bm-moment {
    background: linear-gradient(120deg, #f5f3ff 0%, #ede9fe 100%);
    padding: 2px 5px;
    margin: 0 -1px;
    border-radius: 4px;
    border-bottom: 2px solid #be016d;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
  .ProseMirror .bm-moment:hover {
    background: linear-gradient(120deg, #ede9fe 0%, #ddd6fe 100%);
    border-bottom-color: #7c3aed;
    box-shadow: 0 1px 3px rgba(85, 61, 173, 0.15);
  }
`;

function handleSetLink(editor: Editor) {
  const previousUrl = editor.getAttributes("link").href;
  const url = window.prompt(i18n.t("media.editor.linkUrlPrompt"), previousUrl);
  if (url === null) return;
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}

function handleToggleBanhMiMoment(editor: Editor) {
  const { from, to } = editor.state.selection;
  if (from === to) {
    window.alert(i18n.t("media.editor.momentSelectFirst"));
    return;
  }
  if (editor.isActive(BM_MARK_NAME)) {
    const currentTooltip = editor.getAttributes(BM_MARK_NAME).tooltip || "";
    const tooltip = window.prompt(i18n.t("media.editor.momentPromptExisting"), currentTooltip);
    if (tooltip === null) return;
    if (tooltip === "") {
      editor.commands.unsetBanhMiMoment();
    } else {
      editor.commands.setBanhMiMoment({ tooltip });
    }
  } else {
    const tooltip = window.prompt(i18n.t("media.editor.momentPrompt"));
    if (!tooltip) return;
    editor.commands.setBanhMiMoment({ tooltip });
  }
}

const MenuButton = ({
  onClick,
  isActive,
  disabled,
  children,
  title,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`rounded p-1.5 transition-colors ${
      isActive
        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
  >
    {children}
  </button>
);

// Custom node view for YouTube — renders as placeholder block instead of iframe
const YoutubePlaceholder = ({ node, updateAttributes, deleteNode }: NodeViewProps) => {
  const { t } = useTranslation();
  const src = node.attrs.src || "";
  // Extract video ID for thumbnail
  // Rozbor adresy jen klasifikátorem (vize F8.2) — týž, podle kterého web video přehraje.
  const link = videoLinkOf(src);
  const videoId = link?.provider === "youtube" ? link.id : null;
  // Admin není veřejný web (F8.6 mluví o stránkách webu), náhled z YouTube si nechává.
  // videa-vyjimka: náhled uzlu YouTube v editoru adminu, ne veřejný web
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

  return (
    <NodeViewWrapper className="my-4">
      <div className="group relative flex items-center gap-4 rounded-lg border-2 border-red-200 bg-gradient-to-r from-red-50 to-white p-4 dark:border-red-800 dark:from-red-950/30 dark:to-gray-800">
        {thumbnailUrl ? (
          <div className="relative h-20 w-36 shrink-0 overflow-hidden rounded-md bg-black">
            <img
              src={thumbnailUrl}
              alt={t("media.editor.videoThumbAlt")}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="h-8 w-8 fill-white text-white" />
            </div>
          </div>
        ) : (
          <div className="flex h-20 w-36 shrink-0 items-center justify-center rounded-md bg-red-100 dark:bg-red-900/30">
            <YoutubeIcon className="h-10 w-10 text-red-500" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-1 text-sm font-semibold text-red-700 dark:text-red-400">
            {t("media.editor.youtubeVideo")}
          </div>
          <div className="mb-2 truncate text-xs text-gray-500 dark:text-gray-400">
            {src || t("media.editor.noUrlSet")}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const url = window.prompt(t("media.editor.youtubeUrlPrompt"), src);
                if (url && url !== src) {
                  updateAttributes({ src: url });
                }
              }}
              className="flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
            >
              <Pencil className="h-3 w-3" />
              {src ? t("media.editor.changeUrl") : t("media.editor.setUrl")}
            </button>
            <button
              type="button"
              onClick={() => deleteNode()}
              className="flex items-center gap-1 rounded bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              <Trash2 className="h-3 w-3" /> {t("common.actions.remove")}
            </button>
            {src && (
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-red-600 underline hover:text-red-700"
              >
                {t("media.editor.openVideo")}
              </a>
            )}
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  );
};

const CompactMenuBar = ({ editor }: { editor: Editor | null }) => {
  const { t } = useTranslation();
  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-200 p-2 dark:border-gray-700">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        title={t("media.editor.bold")}
      >
        <Bold className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        title={t("media.editor.italic")}
      >
        <Italic className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        title={t("media.editor.bulletList")}
      >
        <List className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        title={t("media.editor.numberedList")}
      >
        <ListOrdered className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => handleSetLink(editor)}
        isActive={editor.isActive("link")}
        title={t("common.fields.link")}
      >
        <LinkIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => handleToggleBanhMiMoment(editor)}
        isActive={editor.isActive(BM_MARK_NAME)}
        title={t("media.editor.moment")}
      >
        <Sparkles className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title={t("media.editor.undo")}
      >
        <Undo className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title={t("media.editor.redo")}
      >
        <Redo className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

const FullMenuBar = ({
  editor,
  onInsertImage,
}: {
  editor: Editor | null;
  onInsertImage: () => void;
}) => {
  const { t } = useTranslation();
  const insertYouTubeVideo = useCallback(() => {
    if (!editor) return;
    const url = window.prompt(t("media.editor.youtubeUrlPromptExample"));
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url });
  }, [editor, t]);

  if (!editor) return null;

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-200 p-2 dark:border-gray-700">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        title={t("media.editor.bold")}
      >
        <Bold className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        title={t("media.editor.italic")}
      >
        <Italic className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")}
        title={t("media.editor.strikethrough")}
      >
        <Strikethrough className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
        title={t("media.editor.heading1")}
      >
        <Heading1 className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
        title={t("media.editor.heading2")}
      >
        <Heading2 className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        title={t("media.editor.bulletList")}
      >
        <List className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        title={t("media.editor.numberedList")}
      >
        <ListOrdered className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive("blockquote")}
        title={t("media.editor.quote")}
      >
        <Quote className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive("codeBlock")}
        title={t("media.editor.codeBlock")}
      >
        <Code className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => handleSetLink(editor)}
        isActive={editor.isActive("link")}
        title={t("common.fields.link")}
      >
        <LinkIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => handleToggleBanhMiMoment(editor)}
        isActive={editor.isActive(BM_MARK_NAME)}
        title={t("media.editor.moment")}
      >
        <Sparkles className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton onClick={onInsertImage} title={t("media.editor.insertImage")}>
        <ImageIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton onClick={insertYouTubeVideo} title={t("media.editor.insertYoutube")}>
        <YoutubeIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title={t("media.editor.horizontalRule")}
      >
        <Minus className="h-4 w-4" />
      </MenuButton>

      <div className="mx-1 w-px bg-gray-200 dark:bg-gray-700" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title={t("media.editor.undo")}
      >
        <Undo className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title={t("media.editor.redo")}
      >
        <Redo className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

export default function RichTextEditor({
  content,
  onChange,
  placeholder,
  className = "",
  compact = false,
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("media.editor.startTyping");
  const prevContentRef = useRef(content);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  // Preprocess content: ensure blog-video divs have data-youtube-video for TipTap parsing
  const preprocessContent = useMemo(() => {
    if (!content) return content;
    return content.replace(
      /<div class="blog-video"(?!.*data-youtube-video)/g,
      '<div class="blog-video" data-youtube-video=""',
    );
  }, [content]);

  const extensions = useMemo(() => {
    const exts: Extensions = [
      StarterKit.configure(compact ? { heading: false, codeBlock: false } : {}),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary-600 underline hover:text-primary-700",
        },
      }),
      Placeholder.configure({
        placeholder: resolvedPlaceholder,
      }),
      BanhMiMomentMark,
    ];

    if (!compact) {
      exts.push(
        MediaNode,
        Youtube.extend({
          parseHTML() {
            return [
              { tag: "div[data-youtube-video] iframe" },
              { tag: 'iframe[src*="youtube.com"]' },
              { tag: 'iframe[src*="youtu.be"]' },
            ];
          },
          addNodeView() {
            return ReactNodeViewRenderer(YoutubePlaceholder);
          },
        }).configure({
          HTMLAttributes: {
            class: "blog-video",
          },
          width: 640,
          height: 360,
        }),
      );
    }

    return exts;
  }, [compact, resolvedPlaceholder]);

  const editor = useEditor({
    extensions,
    content: preprocessContent,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm dark:prose-invert max-w-none focus:outline-none ${compact ? "min-h-[120px]" : "min-h-[200px]"} p-4`,
      },
    },
  });

  const handleInsertImage = useCallback(() => {
    setImagePickerOpen(true);
  }, []);

  const handleImageSelected = useCallback(
    (mediaId: string | null) => {
      setImagePickerOpen(false);
      if (!editor || !mediaId) return;
      editor.chain().focus().insertContent({ type: MediaNode.name, attrs: { mediaId } }).run();
    },
    [editor],
  );

  // Update editor content when prop changes externally (e.g. form reset)
  useEffect(() => {
    if (editor && content !== prevContentRef.current) {
      const currentHTML = editor.getHTML();
      if (preprocessContent !== currentHTML) {
        editor.commands.setContent(preprocessContent, { emitUpdate: false });
      }
      prevContentRef.current = content;
    }
  }, [content, preprocessContent, editor]);

  return (
    <div
      className={`overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800 ${className}`}
    >
      <style>{EDITOR_STYLES}</style>
      {compact ? (
        <CompactMenuBar editor={editor} />
      ) : (
        <FullMenuBar editor={editor} onInsertImage={handleInsertImage} />
      )}
      <EditorContent editor={editor} />
      {!compact && (
        <MediaPicker
          mode="single"
          hideTrigger
          open={imagePickerOpen}
          onOpenChange={setImagePickerOpen}
          folder="blog"
          value={null}
          onChange={handleImageSelected}
        />
      )}
    </div>
  );
}

export { RichTextEditor };
